import React, { useEffect, useRef } from 'react';
import { Mascot, MascotLevel, MascotMood } from './Mascot';
import { toneForAgent } from './tones';

export interface AgentFaceProps {
  agent: { id: string; enabled: boolean };
  size?: number;
  /** Overrides the status mood (e.g. happy on the "Ready" card, thinking while it works). */
  mood?: MascotMood;
  level?: MascotLevel;
  /**
   * The face's row is hovered: an agent that is on comes fully alive (its eyes
   * follow the pointer), a paused one lets out a drifting "Zzz".
   */
  lively?: boolean;
}

/**
 * An agent's face, the one place that maps an agent to its character: the
 * agent's own tone (`toneForAgent`) and asleep while the agent is paused.
 * Every agent avatar — web and extension — is this.
 */
export const AgentFace: React.FC<AgentFaceProps> = ({ agent, size = 36, mood, level, lively }) => {
  const faceMood = mood ?? (agent.enabled ? 'idle' : 'asleep');
  const face = (
    <Mascot
      size={size}
      tone={toneForAgent(agent.id)}
      mood={faceMood}
      level={lively && faceMood !== 'asleep' ? 'full' : level}
    />
  );
  // Always the same wrapper, so hovering never remounts (and restarts) the face.
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {face}
      {lively && faceMood === 'asleep' && <SleepZzz size={size} />}
    </div>
  );
};

const Z_COUNT = 3;
const Z_CYCLE_MS = 2400;

/** Three small "z"s rising from just inside the face's top corner, each growing and fading out in turn. */
const SleepZzz: React.FC<{ size: number }> = ({ size }) => {
  const refs = useRef<(HTMLSpanElement | null)[]>([]);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const rise = { x: size * 0.2, y: size * 0.4 };
    const anims = refs.current.map((el, i) =>
      el?.animate?.(
        [
          { opacity: 0, transform: 'translate(0, 0) scale(0.6)' },
          { opacity: 1, offset: 0.25 },
          { opacity: 0, transform: `translate(${rise.x}px, ${-rise.y}px) scale(1.15)` },
        ],
        { duration: Z_CYCLE_MS, delay: (i * Z_CYCLE_MS) / Z_COUNT, iterations: Infinity, easing: 'ease-out', fill: 'backwards' },
      ),
    );
    return () => anims.forEach((a) => a?.cancel());
  }, [size]);

  return (
    <span aria-hidden className="pointer-events-none absolute" style={{ top: size * 0.08, insetInlineEnd: size * 0.02 }}>
      {Array.from({ length: Z_COUNT }, (_, i) => (
        <span
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          className="absolute start-0 top-0 font-bold leading-none text-ai-text-secondary opacity-0"
          style={{ fontSize: Math.max(9, Math.round(size * 0.28)) }}
        >
          z
        </span>
      ))}
    </span>
  );
};
